/**
 * RFC-1146 — variant identity and Variants/Archived partitioning.
 *
 * The SINGLE definition of "which variant is this?" for the star/archive
 * feature. Both `VariantsTabContent` (images) and `VideoVariantsTabContent`
 * (videos) import from here; do not add a second, parallel key function.
 *
 * ## Canvas UI 2.0 — two states, not three
 *
 * RFC-1146 originally shipped a 3-state marker (neutral → favourite → disliked)
 * surfaced as a heart plus three independent Like/Neutral/Dislike filter chips.
 * The Canvas UI 2.0 frames collapse that to a BINARY star and two tabs:
 *
 *   starred  → rendered in the **Variants** tab (the default view)
 *   archived → rendered in the **Archived** tab
 *
 * The persistence layer is unchanged — same `variant_markers` table, same
 * display-only semantics. Only the interpretation narrows:
 *
 *   marker === 'disliked'  → archived
 *   anything else (absent, or a legacy 'favorite' row) → starred
 *
 * So `'favorite'` is no longer WRITTEN, but legacy rows carrying it are still
 * read and simply mean "starred" — which is also what an absent marker means.
 * That makes the collapse a pure reinterpretation with no migration: see
 * `setArchived` in `useVariantMarkers`, which writes `'disliked'` to archive and
 * DELETEs the row to restore.
 *
 * ## Why `local_path` and not `result.id` or the array index
 *
 * - **Array index is unstable.** `delete_variant` reindexes (and projects
 *   predating RFC-1147 have histories already front-trimmed on disk by the
 *   removed 20-entry cap), so an index-keyed marker silently re-attaches to a
 *   different image.
 *
 * - **`result.id` is a positional counter, not an identity.** The queued path
 *   builds it as `f"generated_v{spec.sub_index + 1}"`
 *   (`image_generator/agent.py` `apply_result`), so it restarts at
 *   `generated_v1` for every batch and collides across generations. Before
 *   RFC-1147 the append path collided too: `f"generated_v{len(history) + 1}"`
 *   pinned `len(history)` at 20 once the cap was hit, issuing `generated_v21`
 *   forever. That specific collision is gone, but the id is still positional
 *   and still not an identity.
 *
 * - **`local_path` is durably unique per generated asset.** Deleting a variant
 *   removes only the history entry, never the underlying file. Queued video
 *   generation includes its durable job ID in the object key; other asset
 *   paths use collision-aware version allocation. A live path is never
 *   intentionally re-issued.
 *
 * Placeholder entries (empty `local_path`) are NOT markable — they are
 * in-flight or failed slots, not variants. `getVariantKey` returns `null` for
 * them and the marker button renders disabled.
 */

import type { VariantMarker } from '../../../../generated/variantMarkers'

/** The subset of a history entry's `result` needed to identify it. */
export interface VariantResultLike {
  id?: string
  local_path?: string
}

/**
 * Stable marker key for a variant, or `null` when the entry cannot be marked.
 *
 * @param result - The history entry's `result` object.
 * @returns The variant's `local_path`, or `null` for placeholder/empty entries.
 */
export function getVariantKey(
  result: VariantResultLike | null | undefined,
): string | null {
  const localPath = result?.local_path
  if (typeof localPath !== 'string') return null
  const trimmed = localPath.trim()
  return trimmed.length > 0 ? trimmed : null
}

// ---------------------------------------------------------------------------
// Variants / Archived partition
// ---------------------------------------------------------------------------

/** The two views over a node's variants. */
export type VariantTab = 'variants' | 'archived'

/** Both tabs, in display order. */
export const VARIANT_TABS: readonly VariantTab[] = ['variants', 'archived']

/**
 * Tab a node's variant view opens on.
 *
 * Session-only React state, NOT persisted — reopening a node always starts on
 * Variants, matching the pre-Canvas-UI-2.0 behaviour where the dislike bucket
 * was hidden by default.
 */
export const DEFAULT_VARIANT_TAB: VariantTab = 'variants'

/**
 * Whether a marker means "archived".
 *
 * The ONLY place the `'disliked'` wire value is interpreted. Everything else —
 * absent, or a legacy `'favorite'` row — means starred.
 *
 * @param marker - The variant's marker, or undefined when unmarked.
 */
export function isArchivedMarker(marker: VariantMarker | undefined): boolean {
  return marker === 'disliked'
}

/**
 * Which tab a variant belongs to.
 *
 * Evaluated against the LIVE marker map, so unstarring moves the tile to
 * Archived immediately. An earlier revision deferred this to a frozen
 * open-time snapshot; see `useVariantMarkers` for why that made sense for the
 * three-state heart and not for a binary star.
 *
 * @param marker - The variant's current marker, or undefined.
 */
export function tabForMarker(marker: VariantMarker | undefined): VariantTab {
  return isArchivedMarker(marker) ? 'archived' : 'variants'
}

/**
 * Select the variants belonging to one tab.
 *
 * Ordering is deliberately UNCHANGED — markers never reorder the grid. The
 * caller's existing newest-first order is preserved exactly; this only drops
 * entries belonging to the other tab.
 *
 * @param items - Variants in their already-correct display order.
 * @param keyOf - Extracts the marker key for an item (null → unmarkable).
 * @param markers - Snapshot marker map keyed by `local_path`.
 * @param tab - The tab currently being rendered.
 * @returns `visible` in input order, plus how many the tab excluded.
 */
export function selectVariantsForTab<T>(
  items: T[],
  keyOf: (item: T) => string | null,
  markers: Map<string, VariantMarker>,
  tab: VariantTab,
): {
  visible: T[]
  hiddenCount: number
} {
  const visible: T[] = []

  for (const item of items) {
    const key = keyOf(item)
    // Unmarkable entries (placeholders) can never carry a marker, so they are
    // always starred and always live in the Variants tab.
    const marker = key === null ? undefined : markers.get(key)
    if (tabForMarker(marker) === tab) visible.push(item)
  }

  return { visible, hiddenCount: items.length - visible.length }
}

/**
 * Per-tab totals for the tab chips.
 *
 * Deliberately separate from {@link selectVariantsForTab} so the caller can
 * count against the LIVE marker map while membership is evaluated against the
 * frozen snapshot. Deriving the counts from the snapshot too would leave the
 * Archived tab reading "0" immediately after the user archives a variant — the
 * star would update while its own counter disagreed.
 *
 * @param items - Variants in display order.
 * @param keyOf - Extracts the marker key (null → unmarkable → starred).
 * @param markers - The LIVE marker map.
 */
export function countVariantTabs<T>(
  items: T[],
  keyOf: (item: T) => string | null,
  markers: Map<string, VariantMarker>,
): Record<VariantTab, number> {
  const counts: Record<VariantTab, number> = { variants: 0, archived: 0 }
  for (const item of items) {
    const key = keyOf(item)
    const marker = key === null ? undefined : markers.get(key)
    counts[tabForMarker(marker)] += 1
  }
  return counts
}

/**
 * Marker keys with no matching variant in the current history.
 *
 * Expected after `delete_variant`, or on a legacy project whose history was
 * front-trimmed before RFC-1147 removed the cap. A sudden spike means identity
 * drift — the residual risk of keying on `local_path` — which is why this is
 * worth a NAD trace at all. Pure; the caller emits.
 *
 * The companion `deferredHidden` diagnostic is gone with the open-time
 * snapshot: nothing is deferred any more, so there is nothing to observe.
 *
 * @param items - Variants in display order.
 * @param keyOf - Extracts the marker key (null → unmarkable).
 * @param markers - The live marker map.
 */
export function findOrphanMarkerKeys<T>(
  items: T[],
  keyOf: (item: T) => string | null,
  markers: Map<string, VariantMarker>,
): string[] {
  const present = new Set<string>()
  for (const item of items) {
    const key = keyOf(item)
    if (key !== null) present.add(key)
  }

  const orphans: string[] = []
  markers.forEach((_marker, key) => {
    if (!present.has(key)) orphans.push(key)
  })
  return orphans
}
